// align.ts — Figma-style alignment and distribution operations on
// SceneObject positions, computed against each object's live world bbox.
//
// Pure functions: take the selected objects + their bboxes, return a
// patch map of `{ id → { x, z } }` to apply to the scene state. The host
// (SceneBuilder) merges those patches and pushes one undo snapshot per
// alignment action.
//
// Conventions for this codebase:
//   - The floor plane is XZ (Y is up).
//   - "Horizontal" in panel labels means the screen-horizontal axis at
//     the default camera, which is world X.
//   - "Vertical" in panel labels means the depth axis on the floor, world Z.
//
// Object positions in `SceneObject` refer to the object's *origin*, not
// its visual centre. The functions therefore compute the delta needed
// to push each object's bbox-derived edge or centre to the target value,
// then apply that delta to `o.x` / `o.z`. This mirrors Figma's behaviour
// of moving objects, not resizing them.

import type * as THREE from "three";
import type { SceneObject } from "./types";

export type Patch = { x?: number; z?: number };
export type AlignmentResult = Map<string, Patch>;

/**
 * Read the world XZ bounds of every selected object. Caller supplies
 * the bbox map from THREE.Box3.setFromObject(inst). Objects without an
 * entry are skipped (e.g. instances that haven't mounted yet).
 */
type Span = { id: string; minX: number; maxX: number; minZ: number; maxZ: number; cx: number; cz: number };

function spans(
  objects: SceneObject[],
  bboxes: Map<string, THREE.Box3>,
): Span[] {
  const out: Span[] = [];
  for (const o of objects) {
    const b = bboxes.get(o.id);
    if (!b) continue;
    const minX = b.min.x;
    const maxX = b.max.x;
    const minZ = b.min.z;
    const maxZ = b.max.z;
    out.push({
      id: o.id,
      minX, maxX, minZ, maxZ,
      cx: (minX + maxX) / 2,
      cz: (minZ + maxZ) / 2,
    });
  }
  return out;
}

/** Push every selection's left edge (min.x) to the leftmost left edge. */
export function alignLeft(objects: SceneObject[], bboxes: Map<string, THREE.Box3>): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 2) return out;
  const target = Math.min(...sps.map((s) => s.minX));
  for (const o of objects) {
    const s = sps.find((q) => q.id === o.id);
    if (!s) continue;
    out.set(o.id, { x: o.x + (target - s.minX) });
  }
  return out;
}

/** Push every selection's right edge (max.x) to the rightmost right edge. */
export function alignRight(objects: SceneObject[], bboxes: Map<string, THREE.Box3>): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 2) return out;
  const target = Math.max(...sps.map((s) => s.maxX));
  for (const o of objects) {
    const s = sps.find((q) => q.id === o.id);
    if (!s) continue;
    out.set(o.id, { x: o.x + (target - s.maxX) });
  }
  return out;
}

/** Centre every selection on the average centre-X of the selection. */
export function alignCenterX(objects: SceneObject[], bboxes: Map<string, THREE.Box3>): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 2) return out;
  const target = sps.reduce((acc, s) => acc + s.cx, 0) / sps.length;
  for (const o of objects) {
    const s = sps.find((q) => q.id === o.id);
    if (!s) continue;
    out.set(o.id, { x: o.x + (target - s.cx) });
  }
  return out;
}

/** Push every selection's near edge (min.z) to the smallest min.z. */
export function alignTop(objects: SceneObject[], bboxes: Map<string, THREE.Box3>): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 2) return out;
  const target = Math.min(...sps.map((s) => s.minZ));
  for (const o of objects) {
    const s = sps.find((q) => q.id === o.id);
    if (!s) continue;
    out.set(o.id, { z: o.z + (target - s.minZ) });
  }
  return out;
}

/** Push every selection's far edge (max.z) to the largest max.z. */
export function alignBottom(objects: SceneObject[], bboxes: Map<string, THREE.Box3>): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 2) return out;
  const target = Math.max(...sps.map((s) => s.maxZ));
  for (const o of objects) {
    const s = sps.find((q) => q.id === o.id);
    if (!s) continue;
    out.set(o.id, { z: o.z + (target - s.maxZ) });
  }
  return out;
}

/** Centre every selection on the average centre-Z of the selection. */
export function alignCenterZ(objects: SceneObject[], bboxes: Map<string, THREE.Box3>): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 2) return out;
  const target = sps.reduce((acc, s) => acc + s.cz, 0) / sps.length;
  for (const o of objects) {
    const s = sps.find((q) => q.id === o.id);
    if (!s) continue;
    out.set(o.id, { z: o.z + (target - s.cz) });
  }
  return out;
}

/**
 * Distribute selection horizontally (along world X) so the centre-to-
 * centre gaps along X are equal. Anchors the leftmost and rightmost
 * objects (by current centre-X) in place; redistributes the rest evenly
 * between them.
 *
 * Centre-equal (this implementation) is what Figma calls "Distribute
 * horizontal centers" and is the more forgiving of the two distribute
 * modes when objects have mixed sizes. The "equal gap between bounding
 * boxes" mode is a separate operation we can add later if needed.
 */
export function distributeHorizontally(
  objects: SceneObject[],
  bboxes: Map<string, THREE.Box3>,
): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 3) return out;
  // Sort by current centre-X so the anchors pick up cleanly.
  const sorted = [...sps].sort((a, b) => a.cx - b.cx);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalSpan = last.cx - first.cx;
  if (totalSpan <= 0) return out; // all stacked → nothing to do
  const step = totalSpan / (sorted.length - 1);
  for (let i = 1; i < sorted.length - 1; i++) {
    const targetCx = first.cx + i * step;
    const sp = sorted[i];
    const o = objects.find((q) => q.id === sp.id);
    if (!o) continue;
    out.set(o.id, { x: o.x + (targetCx - sp.cx) });
  }
  return out;
}

/** Same as distributeHorizontally but on world Z. */
export function distributeVertically(
  objects: SceneObject[],
  bboxes: Map<string, THREE.Box3>,
): AlignmentResult {
  const out = new Map<string, Patch>();
  const sps = spans(objects, bboxes);
  if (sps.length < 3) return out;
  const sorted = [...sps].sort((a, b) => a.cz - b.cz);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalSpan = last.cz - first.cz;
  if (totalSpan <= 0) return out;
  const step = totalSpan / (sorted.length - 1);
  for (let i = 1; i < sorted.length - 1; i++) {
    const targetCz = first.cz + i * step;
    const sp = sorted[i];
    const o = objects.find((q) => q.id === sp.id);
    if (!o) continue;
    out.set(o.id, { z: o.z + (targetCz - sp.cz) });
  }
  return out;
}

/** Convenience: apply a patch map to a flat object list. */
export function applyPatches(
  objects: SceneObject[],
  patches: AlignmentResult,
): SceneObject[] {
  return objects.map((o) => {
    const p = patches.get(o.id);
    if (!p) return o;
    return { ...o, x: p.x ?? o.x, z: p.z ?? o.z };
  });
}
